/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { useState, useEffect, useContext } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import 'bootstrap/dist/css/bootstrap.min.css'
import { Modal, Button, Form } from 'react-bootstrap'
import { getCategories, Category, getFingerprintsByCategoryThin, FingerprintThin, setCategory, deleteCategory } from '../data'
import styled from 'styled-components'
import { useTheme } from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretDown, faCaretUp, faFastBackward, faFastForward, faStepBackward, faStepForward, faSignInAlt, faMinus, faPlus } from '@fortawesome/free-solid-svg-icons'
import CategoryInfo from './CategoryInfo'
import ClientTable2 from './ClientTable2'
import { StatusContext, StatusContextType } from './StatusContext';

import {
  ColumnDef,
  RowSelectionState,
} from '@tanstack/react-table'

const fcolumns: ColumnDef<Category>[] = [
  {
    header: 'Key',
    accessorKey: 'pvkey',
  },
  {
    header: 'Layer',
    accessorKey: 'layer',
  },
]

const ccolumns: ColumnDef<Category>[] = [
  {
    header: 'Key',
    accessorKey: 'key',
  },
]

function Categories() {
    const navigate = useNavigate();
    const theme: any = useTheme();
    const { category: categoryParam } = useParams<{ category?: string }>();
    const [cSelection, setCSelection] = useState<RowSelectionState>({})
    const [fSelection, setFSelection] = useState<RowSelectionState>({})
    const catkey = Object.keys(cSelection)[0];
    const [fingerData, setFingerData] = useState<FingerprintThin[]>([ ]);
    const [catData, setCatData] = useState<any>([ ]);
    const [showInsertDialog, setShowInsertDialog] = useState(false);
    const [insCatkey, setInsCatkey] = useState<string>("");
    const [reloadCategories, setReloadCategories] = useState<boolean>(true);
    const [insKeyError, setInsKeyError] = useState<string>("Enter a unique category key");
    const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
    const [deleteError, setDeleteError] = useState<string>(""); // For delete error messages
    const statusContext = useContext(StatusContext);
    useEffect(() => {
        if (categoryParam && catData.length > 0) {
            // Check if the category exists in catData
            const categoryExists = catData.some((cat: any) => cat.key === categoryParam);
            if (categoryExists) {
                setCSelection({ [categoryParam]: true });
            }
        }
    }, [categoryParam, catData]);
    useEffect(() => {
        // Update URL when category selection changes (but not from URL param)
        if (catkey && catkey !== categoryParam) {
            navigate(`/categories/${catkey}`, { replace: true });
        }
    }, [catkey, categoryParam, navigate]);
    useEffect(() => {
        async function fetchFingerprints() {
            if (!catkey) return;
            try {
                const response = await getFingerprintsByCategoryThin(catkey);
                setFingerData(response);
            } catch (error : any) {
                statusContext.postError(`Error fetching fingerprints: ${error.message}`);
                console.error('Error fetching fingerprints:', error);
            }
        }  
        fetchFingerprints();
    }, [ cSelection ]);
    useEffect(() => {
        async function fetchCategories() {
            try {
                const response = (await getCategories()) as any;
                setCatData(response);
            } catch (error : any) {
                statusContext.postError(`Error fetching categories: ${error.message}`);
                console.error('Error fetching categories:', error);
            } finally {
                setReloadCategories(false);
            }
        }
        fetchCategories();
    }, [ reloadCategories ] );

    function handleClose() {
        setShowInsertDialog(false);
    }
    function handleDeleteClose() {
        setShowDeleteDialog(false);
    }
    const catkeyValid = insCatkey.length > 0 ? true : false;

    let inputClasses = "form-control";

    return (
    <>
        <div className="container-fluid">
            <div className="row border border-primary" style={{ height: "94vh" }}>
                <div className="col-3 h-100 d-flex flex-column">
                    <h3 className="pt-2 d-flex align-items-center justify-content-between">
                        <span>Categories</span>
                        <div className="d-flex align-items-center gap-2">
                            <span className="badge bg-primary">Total: {catData.length}</span>
                            <div className="d-inline-flex">
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm me-1"
                                    onClick={() => { setShowInsertDialog(true); setInsCatkey(""); }}
                                    aria-label="Add category"
                                    style={{
                                        color: theme.colors.ctext,
                                        borderColor: theme.colors.border || theme.colors.ctext,
                                        backgroundColor: 'transparent',
                                    }}
                                >
                                    <FontAwesomeIcon icon={faPlus} />
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-outline-secondary btn-sm"
                                    onClick={() => {
                                        if (catkey) {
                                            setShowDeleteDialog(true);
                                        }
                                    }}
                                    aria-label="Remove category"
                                    style={{
                                        color: theme.colors.ctext,
                                        borderColor: theme.colors.border || theme.colors.ctext,
                                        backgroundColor: 'transparent',
                                    }}
                                >
                                    <FontAwesomeIcon icon={faMinus}/>
                                </button>
                            </div>
                        </div>
                    </h3>
                    <div className='flex-grow-1 overflow-auto'>
                        <div className='flex-grow-1 overflow-auto'>
                            <ClientTable2 data={catData} columns={ccolumns} rowSelection={cSelection} setRowSelection={setCSelection} keycol="key"/>
                        </div>
                    </div>
                </div>
                { catkey ? (
                    <>
                    <div className="col-6">
                        {catkey && <CategoryInfo catKey={catkey} />}
                    </div>
                    <div className="col-3 h-100 d-flex flex-column">
                       <h5 className="pt-2">{fingerData.length} Related Fingerprints</h5>
                        <div className='flex-grow-1 overflow-auto'>
                            <ClientTable2 data={fingerData} columns={fcolumns} rowSelection={fSelection} setRowSelection={setFSelection} keycol="pvkey" onRowClick={(row: any) => navigate(`/fingerprint/${row.fid}`)}/>
                        </div>
                    </div>
                    </>
                ) : (
                    <div className="col-9 pt-4">
                        <div
                            className="alert"
                            style={{
                                backgroundColor: theme.colors.bg2 || theme.colors.bg,
                                color: theme.colors.ctext,
                                borderColor: theme.colors.border || theme.colors.ctext,
                                borderStyle: 'solid',
                                borderWidth: '1px'
                            }}
                        >
                            Select a category to view details.
                        </div>
                    </div>
                )}
            </div>
        </div>
        <Modal show={showInsertDialog} onHide={handleClose}>
            <Modal.Header closeButton>
                <Modal.Title>Insert Category</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3" controlId="catkey">
                        <Form.Label>Category Key</Form.Label>
                        <Form.Control type="text" placeholder="Unique category key..."
                            className={inputClasses}
                            isInvalid={insKeyError !== ""} // Set invalid if there's an error
                            value={insCatkey}
                            onChange={e => {
                                let newValue = String(e.target.value);
                                setInsCatkey(newValue);
                                if (newValue.length == 0) {
                                    setInsKeyError("Category key cannot be empty.");
                                } else if (!(/^[a-z0-9_]+$/.test(newValue))) {
                                    // Validate against regex for allowed characters
                                    setInsKeyError("Category key must be lower case alphanumeric and can include underscores.");
                                } else if (catData.some((cat: any) => cat.key === newValue)) {
                                    // Check if the key already exists in the categories
                                    setInsKeyError("Category key already exists. Please choose a unique key.");
                                } else {
                                    // Key is unique
                                    setInsKeyError(""); // Clear error if valid
                                }
                            }}
                         />
                        {insKeyError !== ""? (
                            <Form.Text className="invalid-feedback">
                                {insKeyError}
                            </Form.Text>
                        ) : (
                            <Form.Text className="text-success">
                                Looks good!
                            </Form.Text>
                        )}
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
                <Button variant="primary" onClick={() => {
                    async function setCat(key: string) {
                        await setCategory({
                            key,
                            title: "",
                            description: "",
                            presentation: {}
                        });
                        setShowInsertDialog(false);
                        setReloadCategories(true); // Reload categories to reflect the new one
                        setCSelection({
                            [insCatkey]: true // This will select the new category in the table
                        });
                    }
                    setCat(insCatkey);
                }}>
                    Create Category
                </Button>
            </Modal.Footer>
        </Modal>

        <Modal show={showDeleteDialog} onHide={handleDeleteClose}>
            <Modal.Header closeButton>
                <Modal.Title>Delete Category</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>Are you sure you want to delete the category <strong>{catkey}</strong>? This action cannot be undone.</p>
                <p>There are {fingerData.length} fingerprints that will be disassociated from the category.</p>
                {deleteError && (
                    <div className="alert alert-danger">
                        {deleteError}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleDeleteClose}>
                    Close
                </Button>
                <Button variant="primary" onClick={() => {
                    async function delCat(key: string) {
                        try {
                            await deleteCategory(key);
                            setShowDeleteDialog(false);
                            setReloadCategories(true);
                            setDeleteError("");
                            setCSelection({}); // Clear selection after deletion
                        } catch (error : any) {
                            // Handle error if deletion fails
                            console.error('Error deleting category:', error);
                            setDeleteError(`Failed to delete category: ${error.message}`);
                            return;
                        }
                    }
                    delCat(catkey);
                }}>
                    Delete Category
                </Button>
            </Modal.Footer>
        </Modal>
    </>
    );
}

//Styles
const SortIcon = styled.span`
    margin-left: 5px;
`

export default Categories;